import { useState } from "react";
import { apiClient } from "@/services/apiClient";

export interface CaptionVariant {
  id: string;
  text: string;
  characterCount: number;
}

export interface CaptionGenerationResult {
  captions: CaptionVariant[];
  altText: string;
  generationId?: number;
}

export interface QueuedGenerationResult {
  jobId: string;
  status: "queued";
}

export const useCaptionGenerator = (clientId: string, postId: string) => {
  const [error, setError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  /**
   * Queue caption generation as an async job.
   * Returns the jobId immediately. Use useJobSSE to track completion.
   */
  const queueCaptionGeneration = async (
    description: string,
    keywords: string[],
  ): Promise<QueuedGenerationResult | null> => {
    setError(null);

    try {
      const data = await apiClient.post(
        `/api/client/${clientId}/posts/${postId}/generate-captions`,
        {
          image_description: description,
          keywords,
        }
      );

      if (data.success && data.jobId) {
        return {
          jobId: data.jobId,
          status: "queued",
        };
      } else {
        setError(data.error || "Failed to queue caption generation");
        return null;
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to queue caption generation";
      setError(message);
      return null;
    }
  };

  const saveCaption = async (
    caption: string,
    generationId: number | null,
    selectedVariantId: string | null,
  ): Promise<{ success: boolean; post?: unknown }> => {
    setIsSaving(true);
    setError(null);

    try {
      const data = await apiClient.put(
        `/api/client/${clientId}/posts/${postId}/caption`,
        {
          caption,
          generation_id: generationId,
          selected_variant_id: selectedVariantId,
        }
      );

      if (data.success) {
        return { success: true, post: data.post };
      } else {
        setError(data.error || "Failed to save caption");
        return { success: false };
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Failed to save caption";
      setError(message);
      return { success: false };
    } finally {
      setIsSaving(false);
    }
  };

  return {
    queueCaptionGeneration,
    saveCaption,
    isSaving,
    error,
    clearError: () => setError(null),
  };
};
